import { RadioButton } from "primereact/radiobutton";
import styled from "styled-components";
import {
  BenefitServiceLine,
  IBenefitCoverageRow,
} from "types/BenefitSelection/BenefitSelection";

import { SERVICE_LINES, serviceLineLabel } from "./constants";

const Row = styled.label<{ $selected: boolean }>`
  display: grid;
  grid-template-columns: 1.5rem minmax(12rem, 2fr) 1fr 1fr;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 0.75rem;
  border: 1px solid
    ${({ $selected }) =>
      $selected ? "var(--primary-color)" : "var(--surface-200)"};
  background: ${({ $selected }) =>
    $selected ? "var(--primary-50, #f4f2ff)" : "#fff"};
  border-radius: 6px;
  cursor: pointer;

  & + & {
    margin-top: 0.35rem;
  }
`;

const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: 1.5rem minmax(12rem, 2fr) 1fr 1fr;
  gap: 0.75rem;
  padding: 0 0.75rem 0.35rem;
  /* The app's own DataTable header: not a caption, and never lighter than the rows beneath it. */
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-label);
`;

const Figure = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0.6rem 0.9rem;
  border-radius: 6px;
  background: var(--surface-100);
  min-width: 8rem;

  strong {
    font-size: 1.25rem;
  }

  span {
    font-size: 0.75rem;
    color: var(--text-color-secondary);
  }
`;

/**
 * The counts for one service line, out of the checks in the window.
 *
 * A service line the payers never broke out has NO row in `coverage.byServiceLine` — the API groups
 * over rows that exist. Zero is the right reading, and it is rendered as `0 of N` rather than as a
 * dash so that "the payer never sends this" and "we have not looked" do not share a symbol.
 */
export const coverageFor = (
  coverage: IBenefitCoverageRow[],
  serviceLine: BenefitServiceLine
): IBenefitCoverageRow =>
  coverage.find((row) => row.serviceLine === serviceLine) ?? {
    serviceLine,
    copayReported: 0,
    coinsuranceReported: 0,
  };

interface IServiceLinePickerProps {
  value: BenefitServiceLine | null;
  /** What the account reads today, so the impact box can say "vs current selection". */
  saved: BenefitServiceLine | null;
  coverage: IBenefitCoverageRow[];
  checks: number;
  windowDays: number;
  disabled?: boolean;
  onChange: (value: BenefitServiceLine) => void;
}

/**
 * CLE-2792 — step 2: which of the payer's service lines this account's copay and co-insurance are
 * read from.
 *
 * 🚨 THE COUNTS SAY WHAT THEY COUNT, IN FULL, EVERY TIME. "206 of 298" is how often the payer's
 * response carried that service line WITH a copay on it, out of this account's checks in the
 * window. It is not a ranking, not a recommendation and not evidence the line is the right one —
 * the right one is a fact about how the facility bills. A bare "206" here would read as a score.
 *
 * ⚠️ THE WINDOW IS ON THE SCREEN because a low count has two causes that look identical: the payer
 * rarely breaks the line out, or we have not asked often enough yet. A NEW ACCOUNT HAS NO COUNTS AT
 * ALL, which is why nothing on this card depends on them — and why the impact box is HIDDEN rather
 * than filled with a sentence explaining that it has nothing to say (review round 3). The window is read from the response, not
 * written into the sentence, so a changed window cannot leave the screen saying "30 days".
 */
export function ServiceLinePicker({
  value,
  saved,
  coverage,
  checks,
  windowDays,
  disabled,
  onChange,
}: IServiceLinePickerProps) {
  const selected = value ? coverageFor(coverage, value) : null;
  const current = saved ? coverageFor(coverage, saved) : null;
  const changed = value !== null && saved !== null && value !== saved;

  return (
    <div
      className="flex flex-column gap-3"
      data-testid="benefitSelection.serviceLinePicker"
    >
      <div>
        <HeaderRow>
          <span />
          <span>Service line</span>
          <span>Copay reported</span>
          <span>Co-ins reported</span>
        </HeaderRow>

        {SERVICE_LINES.map((line) => {
          const counts = coverageFor(coverage, line.value);
          return (
            <Row
              key={line.value}
              $selected={value === line.value}
              htmlFor={`serviceLine-${line.value}`}
              data-testid={`benefitSelection.serviceLine.${line.value}`}
            >
              <RadioButton
                inputId={`serviceLine-${line.value}`}
                name="benefitSelectionServiceLine"
                value={line.value}
                disabled={disabled}
                checked={value === line.value}
                onChange={() => onChange(line.value)}
              />
              <span className="flex flex-column">
                <span className="font-medium text-sm">{line.label}</span>
                <span className="text-xs text-color-secondary">
                  {line.pverifyNames}
                </span>
              </span>
              <span className="text-sm">
                {checks > 0
                  ? `${counts.copayReported} of ${checks}`
                  : "No checks yet"}
              </span>
              <span className="text-sm">
                {checks > 0
                  ? `${counts.coinsuranceReported} of ${checks}`
                  : "No checks yet"}
              </span>
            </Row>
          );
        })}
      </div>

      <span
        className="text-sm text-color-secondary"
        data-testid="benefitSelection.coverageExplainer"
      >
        Based on this account&apos;s eligibility checks in the last {windowDays}{" "}
        days.
      </span>

      {selected && value && checks > 0 && (
        <div
          className="flex flex-column gap-2 p-3 border-round border-1 border-gray-200"
          data-testid="benefitSelection.impactBox"
        >
          <span className="font-medium text-sm">
            {serviceLineLabel(value)} is this account&apos;s service line
          </span>
          <span className="text-sm text-color-secondary">
            {`Payers reported a copay for this service line in ${
              selected.copayReported
            } of the last ${checks} checks, and a co-insurance in ${
              selected.coinsuranceReported
            }. In the other ${
              checks - selected.copayReported
            } checks no copay was reported for it, so those estimates get no copay unless the payer reported an error.`}
          </span>

          <div className="flex flex-wrap gap-2 align-items-center">
            <Figure>
              <strong>{selected.copayReported}</strong>
              <span>copay reported</span>
            </Figure>
            <Figure>
              <strong>{selected.coinsuranceReported}</strong>
              <span>co-ins reported</span>
            </Figure>
            <Figure>
              <strong>{checks - selected.copayReported}</strong>
              <span>no copay</span>
            </Figure>
            <span
              className="text-sm text-color-secondary"
              data-testid="benefitSelection.impactBox.delta"
            >
              {changed && current
                ? `${
                    selected.copayReported - current.copayReported >= 0
                      ? "+"
                      : ""
                  }${
                    selected.copayReported - current.copayReported
                  } copay reported vs current selection (${serviceLineLabel(
                    saved
                  )})`
                : "— vs current selection"}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
